import React, { ReactElement } from 'react';

import { WEB_APP_SIGNUP_URL } from '../../../../../globals';
import { Assets } from '../../../../../utils/assets';
import MaxWidth from '../../../../shared/MaxWidth';
import { SOCIAL_CARDS } from '../../constants';
import { ActivityCardScroll } from '../ActivityCardScroll';
import { Card } from '../Card';
import { FAQ } from '../FAQ';
import { GenreCardScroll } from '../GenreCardScroll';
import { SocialCard } from '../SocialCard';
import * as S from './Content.styles';

export const Content = (): ReactElement => {
  const handleClickCta = () => {
    window.location.href = WEB_APP_SIGNUP_URL;
  };
  
  return (
    <S.Wrapper>
      <MaxWidth>
        <S.StimulateSection>
          <S.StimulateImageWrapper>
            <S.Image 
              alt={Assets.images.adhdStimulate.alt} 
              src={Assets.images.adhdStimulate.url}
            />
          </S.StimulateImageWrapper>
          <S.SectionTextContainer>
            <S.SectionTitle>
              Stimulate your brain, avoid distraction
            </S.SectionTitle>
            <S.SectionDescription>
              Users experience a <span>119% increase in focus brainwaves</span>, and{' '}
              <span>15% better focus in a standard attention task.</span>
            </S.SectionDescription>
            <S.MobileStimulateImageWrapper>
              <S.Image 
                alt={Assets.images.adhdStimulate.alt} 
                src={Assets.images.adhdStimulate.url}
              />
            </S.MobileStimulateImageWrapper>
            <S.ButtonLink onClick={handleClickCta}>
              <span>FOCUS WITH SCIENCE</span>
              <img alt="arrow" src="https://cdn.brain.fm/icons/arrow-right.svg" />
            </S.ButtonLink>
          </S.SectionTextContainer>
        </S.StimulateSection>
      </MaxWidth>
      <S.PersonalizedSection>
        <S.LeftBlurImage 
          alt={Assets.images.adhdLeftBlur.alt} 
          src={Assets.images.adhdLeftBlur.url}
        />
        <MaxWidth>
          <S.PersonalizedContent>
            <S.SectionTitle>
              Music personalized to your brain type
            </S.SectionTitle>
            <S.PersonalizedCardsWrapper>
              <Card 
                description="Diagnosed, or undiagnosed but severely impacts your life." 
                imageSrc={Assets.images.adhdClinical.url}
                title="Clinical ADHD" 
              />
              <Card 
                description="Clear ADHD symptoms but not passing threshold for diagnosis" 
                imageSrc={Assets.images.adhdSubclinical.url} 
                title="Subclinical ADHD" 
              />
              <Card 
                description="Some ADHD symptoms make it hard to get work done at times." 
                imageSrc={Assets.images.adhdMildAttentional.url} 
                title="Mild attentional challenges" 
              />
            </S.PersonalizedCardsWrapper>
          </S.PersonalizedContent>
        </MaxWidth>
      </S.PersonalizedSection>
      <MaxWidth>
        <S.SocialSection>
          <S.SectionTitle>
          Join 250,000+ ADHD users...
          </S.SectionTitle>
          <S.SectionDescription style={{ textAlign: 'center' }}>
            Our ADHD users win the day, everyday — see what they have to say!
          </S.SectionDescription>
          <S.SocialCardsWrapper>
            {SOCIAL_CARDS.map((card, index) => (
              <SocialCard
                key={index}
                description={card.description}
                name={card.name}
                profileImageUrl={card.profileImageUrl}
                username={card.username}
              />
            ))}
          </S.SocialCardsWrapper>
          <S.ButtonLink onClick={handleClickCta}>
            <span>HEAR IT FOR YOURSELF</span>
            <img alt="arrow" src="https://cdn.brain.fm/icons/arrow-right.svg" />
          </S.ButtonLink>
        </S.SocialSection>

        <S.HowItWorksSection>
          <S.HowItWorksImageWrapper>
            <S.Image 
              alt={Assets.images.adhdHowItWorks.alt} 
              src={Assets.images.adhdHowItWorks.url}
            />
          </S.HowItWorksImageWrapper>
          <S.HowItWorksContent>
            <S.SectionTitle>
              How it works
            </S.SectionTitle>
            <S.StepsContainer>
              <S.StepItem>
                <S.StepImage alt={Assets.icons.notebookPen.alt} src={Assets.icons.notebookPen.url} />
                <S.StepContent>
                  <S.StepTitle>Take the quiz</S.StepTitle>
                  <S.StepDescription>We’ll learn about your brain and start personalizing your music.</S.StepDescription>
                </S.StepContent>
              </S.StepItem>
              <S.StepItem>
                <S.StepImage alt={Assets.icons.zap.alt} src={Assets.icons.zap.url} />
                <S.StepContent>
                  <S.StepTitle>Turn on ADHD Mode in the app</S.StepTitle>
                  <S.StepDescription>If it’s not already selected, turn on High Neural Effect Level (our ADHD mode) in your Preferences 
                    <S.PreferenceIconImage alt={Assets.icons.preferences.alt} src={Assets.icons.preferences.url} />
                  </S.StepDescription>
                </S.StepContent>
              </S.StepItem>
              <S.StepItem>
                <S.StepImage alt={Assets.icons.timer.alt} src={Assets.icons.timer.url} />
                <S.StepContent>
                  <S.StepTitle>Commit to at least 1 hour of work</S.StepTitle>
                  <S.StepDescription>Sit down with a task and just hit play.</S.StepDescription>
                </S.StepContent>
              </S.StepItem>
            </S.StepsContainer>
            <S.ButtonLink onClick={handleClickCta}>
                <span>GET STARTED</span>
                <img alt="arrow" src="https://cdn.brain.fm/icons/arrow-right.svg" />
              </S.ButtonLink>
          </S.HowItWorksContent>
        </S.HowItWorksSection>

        <S.MusicTaskSection>
          <S.SectionTitle>
            Select music based on your task
        </S.SectionTitle>
        <ActivityCardScroll />
        </S.MusicTaskSection>
      </MaxWidth>

      <S.GenreSection>
        <S.RightBlurImage 
          alt={Assets.images.adhdRightBlur.alt} 
          src={Assets.images.adhdRightBlur.url}
        />
        <MaxWidth>
          <S.GenreContent>
            <S.SectionTitle>
              Pick from top genres
            </S.SectionTitle>
            <GenreCardScroll />
          </S.GenreContent>
        </MaxWidth>
      </S.GenreSection>

      <MaxWidth>
        <S.BenefitsSections>
          <S.BenefitsBlurImage alt={Assets.images.adhdBenefitsBlur.alt} src={Assets.images.adhdBenefitsBlur.url} />
          <S.BenefitsSection>
            <S.BenefitsIconWrapper>
              <S.BenefitsIcon alt={Assets.images.adhdRedZap.alt} src={Assets.images.adhdRedZap.url} />
            </S.BenefitsIconWrapper>
            <S.BenefitsDescription>
              <span>ADHD Mode</span> made for extra stimulation & less distraction
            </S.BenefitsDescription>
          </S.BenefitsSection>
          <S.BenefitsSection>
            <S.BenefitsIconWrapper>
              <S.BenefitsIcon alt={Assets.images.adhdGreenBrain.alt} src={Assets.images.adhdGreenBrain.url} />
            </S.BenefitsIconWrapper>
            <S.BenefitsDescription>
              <span>Personalized</span> to your brain type and favorite music
            </S.BenefitsDescription>
          </S.BenefitsSection>
          <S.BenefitsSection>
            <S.BenefitsIconWrapper>
              <S.BenefitsIcon alt={Assets.images.adhdBluePomodoro.alt} src={Assets.images.adhdBluePomodoro.url} />
            </S.BenefitsIconWrapper>
            <S.BenefitsDescription>
              <span>Productivity tools</span> to keep you motivated
            </S.BenefitsDescription>
          </S.BenefitsSection>
        </S.BenefitsSections>
        <S.FAQSection>
          <FAQ />
        </S.FAQSection>
      </MaxWidth>
    </S.Wrapper>
  );
};
